import type { GameModel, ReadOnlyGameModel } from 'yage/game/GameModel';
import { Transform } from 'yage/schemas/entity/Transform';
import { AirportAgent, AirportBag, AirportPassenger, AirportStaff } from '../components/index.js';
import { ROUTE, onPad, stationPad, DISPATCH } from '../domain/config.js';
import { shiftsActive } from '../domain/act2.js';
import { boardingWindowOpen, earlyBoardingWindowOpen } from '../domain/timetable.js';
import { planes } from './operations.js';
import { tier } from '../domain/airports.js';
import { boardingWorkPoint } from '../domain/queues.js';
import { ids, stations, state } from './world.js';

export function worksDesk(staff: { role: string; stage: number }): boolean {
    return staff.role === 'clerk' || staff.role === 'crew' && staff.stage >= 0;
}

/** A helper finishes their current line or bag load before covering another job. */
export function crewTask(g: GameModel | ReadOnlyGameModel, id: number): number {
    const helper = g.getTypedUnsafe(AirportStaff, id), ss = stations(g);
    const bags = ids(g, AirportBag);
    if (bags.some(b => g.getTypedUnsafe(AirportBag, b).carrier === id)) return -1;
    // A full rack blocks desk service, so clear it before trying to finish that line.
    if (ss[1].unlocked && bags.length >= 24 && bags.some(b => g.getTypedUnsafe(AirportBag, b).carrier < 0)) return -1;
    const ready = ids(g, AirportPassenger).filter(id => { const p = g.getTypedUnsafe(AirportPassenger,id); return !p.arriving && p.stage === 6 && [0,1,2,6,7].includes(p.state); }).length;
    const boarding = planes(g).some(p => boardingWindowOpen(p, g.frame) || earlyBoardingWindowOpen(p,g.frame) && ready > 0);
    const waiting = (stage: number) => ids(g, AirportPassenger).filter(id => {
        const p = g.getTypedUnsafe(AirportPassenger, id);
        return !p.arriving && p.stage === stage && (p.state < 3 || stage === 6 && [6,7].includes(p.state));
    }).length;
    const covered = (stage: number) => stage === 6 ? boardingCrewGate(g, id) < 0 : ids(g, AirportAgent).some(a => onPad('work', g.getTypedUnsafe(Transform, a), stationPad(stage, 'work')))
        || ids(g, AirportStaff).some(a => a !== id && worksDesk(g.getTypedUnsafe(AirportStaff, a)) && g.getTypedUnsafe(AirportStaff, a).stage === stage);
    const useful = (stage: number) => stage >= 0 && ss[stage]?.unlocked && (stage !== 6 || boarding) && waiting(stage) > 0 && !covered(stage);
    // A dispatcher stays at Flight Dispatch: its work is the schedule, not a passenger queue (MGT-011).
    if (helper.homeStage === DISPATCH && ss[DISPATCH]?.unlocked && shiftsActive(state(g))) return DISPATCH;
    if (useful(helper.stage)) return helper.stage;
    if (useful(helper.homeStage)) return helper.homeStage;
    const next = [...ROUTE].reverse().find(useful);
    if (next !== undefined) return next;
    if (ss[1].unlocked && ids(g, AirportBag).some(b => g.getTypedUnsafe(AirportBag, b).carrier < 0)) return -1;
    if (ss[1].unlocked && !covered(1)) return 1;
    return helper.homeStage;
}

/** Keep working a gate's load, then cover the next unattended flight without player assignment. */
export function boardingCrewGate(g: GameModel | ReadOnlyGameModel, id: number): number {
    const e = state(g), at = g.getTypedUnsafe(Transform, id);
    const occupied = (gate: number) => {
        const pad = boardingWorkPoint(gate, tier(e).code);
        return ids(g, AirportAgent).some(a => onPad('work', g.getTypedUnsafe(Transform, a), pad)) || ids(g, AirportStaff).some(a => a !== id && worksDesk(g.getTypedUnsafe(AirportStaff, a)) && g.getTypedUnsafe(AirportStaff, a).stage === 6 && onPad('work', g.getTypedUnsafe(Transform, a), pad));
    };
    const ready = ids(g, AirportPassenger).filter(a => { const p = g.getTypedUnsafe(AirportPassenger, a); return !p.arriving && p.stage === 6 && [0, 1, 2, 6, 7].includes(p.state); }).length;
    const candidates = planes(g).filter(p => p.phase === 0 && p.boarded < p.seats && (p.boardingCalled || boardingWindowOpen(p, g.frame) || earlyBoardingWindowOpen(p, g.frame) && ready > 0) && !occupied(p.gate));
    const current = candidates.find(p => onPad('work', at, boardingWorkPoint(p.gate, tier(e).code)));
    return current?.gate ?? candidates.sort((a, b) => a.scheduledDepartureFrame - b.scheduledDepartureFrame || a.gate - b.gate)[0]?.gate ?? -1;
}
